import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { FileText, Loader2, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n";

const API = "/api";

const DOC_KEY = {
  terms: "app.legal.doc_terms",
  privacy: "app.legal.doc_privacy",
  complaints: "app.legal.doc_complaints",
};

/** Legal re-acceptance prompt. Shown ONLY when the published version the user
 *  has accepted is older than the current one. Records client="web".
 *  RC-WEB-5 GATE 16 — localized in every supported locale. */
export const LegalReacceptanceBanner = () => {
  const { isAuthenticated } = useAuth();
  const { t, lang } = useI18n();
  const docTitle = (type) => (DOC_KEY[type] ? t(DOC_KEY[type]) : type);
  const [pending, setPending] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);
  const [body, setBody] = useState("");
  const [working, setWorking] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const r = await axios.get(`${API}/me/legal-acceptances`, { params: { locale: "auto" } });
      setPending(r.data.pending || []);
    } catch (_e) { /* silent: never blocks the dashboard */ }
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const openDocument = async (doc) => {
    setOpenDoc(doc);
    setBody("");
    try {
      const r = await axios.get(`${API}/legal/${doc.doc_type}`, { params: { locale: doc.locale || lang } });
      setBody(r.data.body || "");
    } catch (_e) {
      setBody(t("app.legal.load_error"));
    }
  };

  const accept = async (doc) => {
    setWorking(true);
    try {
      await axios.post(`${API}/legal/accept`, {
        doc_type: doc.doc_type, locale: doc.locale, version: doc.version, client: "web",
      });
      toast.success(t("app.legal.accepted_ok"));
      setOpenDoc(null);
      setPending((prev) => prev.filter((p) => p.doc_type !== doc.doc_type));
    } catch (_e) {
      toast.error(t("app.legal.accept_error"));
    } finally {
      setWorking(false);
    }
  };

  if (!isAuthenticated || dismissed || pending.length === 0) return null;

  return (
    <>
      <div role="region" aria-label={t("app.legal.banner_region")}
           className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3"
           data-testid="legal-reacceptance-banner">
        <FileText className="w-5 h-5 text-amber-700 shrink-0" aria-hidden="true" />
        <p className="text-sm text-amber-800 flex-1 min-w-[200px]">
          {t("app.legal.banner_text", {
            docs: pending.map((p) => docTitle(p.doc_type)).join(", "),
          })}
        </p>
        <Button onClick={() => openDocument(pending[0])}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                data-testid="legal-reacceptance-review">{t("app.legal.review")}</Button>
        <button type="button" onClick={() => setDismissed(true)}
                aria-label={t("app.legal.dismiss")}
                className="text-amber-800/80 hover:text-amber-900"
                data-testid="legal-reacceptance-dismiss">
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      {openDoc && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
             role="dialog" aria-modal="true" aria-label={docTitle(openDoc.doc_type)}
             data-testid="legal-reacceptance-modal">
          <div className="bg-white border border-slate-200 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-base md:text-lg font-semibold text-[#0B2545]">
                {docTitle(openDoc.doc_type)} · v{openDoc.version}
              </h2>
              <button type="button" onClick={() => setOpenDoc(null)}
                      aria-label={t("app.legal.close")} className="text-slate-500 hover:text-[#0B2545]"
                      data-testid="legal-modal-close">
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto text-sm text-slate-700 whitespace-pre-wrap flex-1"
                 data-testid="legal-modal-body">
              {body || <Loader2 className="w-5 h-5 animate-spin text-[#1B84FF]" />}
            </div>
            <div className="p-5 border-t border-slate-200 flex flex-wrap gap-3">
              <Button onClick={() => accept(openDoc)} disabled={working || !body}
                      className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold"
                      data-testid="legal-modal-accept">
                {working ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : t("app.legal.accept")}
              </Button>
              <Button variant="ghost" onClick={() => setOpenDoc(null)}
                      className="text-slate-700" data-testid="legal-modal-later">{t("app.legal.later")}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LegalReacceptanceBanner;
